import { useEffect, useRef } from 'react';

const Confetti = ({ trigger = false, onComplete }) => {
  const canvasRef = useRef(null);

  useEffect(() => {
    if (!trigger) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const confetti = [];
    const confettiCount = 150;
    const gravity = 0.5;
    const terminalVelocity = 5;
    const drag = 0.075;
    const colors = [
      { front: '#0B2D6E', back: '#1e40af' }, // theme-blue
      { front: '#F5C542', back: '#fbbf24' }, // theme-yellow
      { front: '#10b981', back: '#059669' }, // green
      { front: '#ef4444', back: '#dc2626' }, // red
      { front: '#8b5cf6', back: '#7c3aed' }, // purple
    ];

    // Set canvas size
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    // Confetti class
    class ConfettiParticle {
      constructor() {
        this.randomModifier = Math.random() * 0.5;
        this.color = colors[Math.floor(Math.random() * colors.length)];
        this.dimensions = {
          x: Math.random() * 10 + 5,
          y: Math.random() * 10 + 5,
        };
        this.position = {
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height - canvas.height,
        };
        this.rotation = Math.random() * Math.PI * 2;
        this.velocity = {
          x: Math.random() * 50 - 25,
          y: Math.random() * 50 + 50,
        };
        this.opacity = Math.random() + 0.5;
        this.drag = drag + Math.random() * 0.1;
      }

      update() {
        // Apply drag
        this.velocity.x *= 1 - this.drag;
        this.velocity.y *= 1 - this.drag;

        // Apply gravity
        this.velocity.y = Math.min(this.velocity.y + gravity, terminalVelocity);

        // Update position
        this.position.x += this.velocity.x;
        this.position.y += this.velocity.y;
        this.rotation += this.velocity.x * 0.01;

        // Reset if off screen
        if (this.position.y > canvas.height) {
          this.position.y = -this.dimensions.y;
          this.position.x = Math.random() * canvas.width;
        }
        if (this.position.x > canvas.width + this.dimensions.x) {
          this.position.x = -this.dimensions.x;
        } else if (this.position.x < -this.dimensions.x) {
          this.position.x = canvas.width + this.dimensions.x;
        }
      }

      draw() {
        ctx.save();
        ctx.translate(this.position.x, this.position.y);
        ctx.rotate(this.rotation);
        ctx.globalAlpha = this.opacity;

        // Front
        ctx.fillStyle = this.color.front;
        ctx.fillRect(-this.dimensions.x / 2, -this.dimensions.y / 2, this.dimensions.x, this.dimensions.y);

        // Back
        ctx.fillStyle = this.color.back;
        ctx.fillRect(-this.dimensions.x / 2, -this.dimensions.y / 2, this.dimensions.x, this.dimensions.y * 0.6);

        ctx.restore();
      }
    }

    // Create confetti particles
    for (let i = 0; i < confettiCount; i++) {
      confetti.push(new ConfettiParticle());
    }

    // Animation loop
    let animationFrameId;
    let frameCount = 0;
    const maxFrames = 300; // ~5 seconds at 60fps

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      confetti.forEach((particle) => {
        particle.update();
        particle.draw();
      });

      frameCount++;
      if (frameCount < maxFrames) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        // Cleanup
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        if (onComplete) onComplete();
      }
    };

    animate();

    // Handle resize
    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    window.addEventListener('resize', handleResize);

    return () => {
      cancelAnimationFrame(animationFrameId);
      window.removeEventListener('resize', handleResize);
    };
  }, [trigger, onComplete]);

  return (
    <canvas
      ref={canvasRef}
      className="fixed inset-0 pointer-events-none z-50"
      style={{ width: '100%', height: '100%' }}
    />
  );
};

export default Confetti;






